/**
 * /app/computed-tags — define derived tags via math expressions.
 *
 * Cards mirror the alarms Rules-tab pattern. Click "+ New computed tag"
 * to open the editor drawer; values stream live so users see what their
 * formula produces before they save.
 *
 * Phase 0: in-memory store + math.js client-side evaluator. The shape
 * the editor produces (`ComputedTagDef`) is what /v1/iot/computed-tags
 * persists in Phase 1.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  CheckCircle2,
  Code2,
  Copy,
  Edit3,
  History,
  Plus,
  Sigma,
  Sparkles,
  Trash2,
  Wand2,
  X,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { PageContainer } from "@/routes/_layout";
import { PageHeader } from "@/lib/ui/page-header";
import { Badge } from "@/lib/ui/badge";
import { Button } from "@/lib/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/lib/ui/card";
import { Input } from "@/lib/ui/input";
import { EmptyState } from "@/lib/ui/empty-state";
import { cn } from "@/lib/ui/cn";
import { useComputedTags } from "@/lib/compute/store";
import { useOrg } from "@/lib/org-context";
import { compile, validate } from "@/lib/compute/engine";
import { useLatestTagValues } from "@/lib/compute/preview";
import { type CatalogTag } from "@/lib/compute/__fixtures";
import { ORG_TAG_PAGE_CAP, makeAliasFromTag, useTagCatalog } from "@/lib/compute/catalog";
import { FUNCTION_GROUPS } from "@/lib/compute/function-catalog";
import { PRESETS, presetToDraft, type ComputedTagPreset } from "@/lib/compute/presets";
import { DATA_KIND_LABEL, type DataKind } from "@/lib/dashboard/data-kinds";
import type {
  ComputedTagDef,
  ComputedTagInput,
  ComputedTagTrigger,
} from "@/lib/compute/types";

const ALL_DATA_KINDS: DataKind[] = [
  "analog_scalar",
  "analog_percentage",
  "count_scalar",
  "derived_kpi",
  "categorical_enum",
  "categorical_distribution",
  "event_stream",
  "boolean_state",
  "string_label",
];

export function ComputedTags() {
  const store = useComputedTags();
  const { can } = useOrg();
  // Computed-tag authoring (create/edit/delete/duplicate) → model.write.
  const canWrite = can("model.write");
  const [editing, setEditing] = useState<ComputedTagDef | "new" | null>(null);

  return (
    <PageContainer className="max-w-7xl">
      <PageHeader
        breadcrumb={
          <>
            <Link to="/app" className="hover:text-on-surface">
              App
            </Link>
            <span aria-hidden>/</span>
            <span className="text-on-surface">Computed tags</span>
          </>
        }
        title="Computed tags"
        description="Make a new tag from other tags with a formula: a ratio, a corrected reading, or a measure made of several. A computed tag can be used anywhere a measured one can."
        actions={
          <>
            <Badge variant="info">{store.tags.length} tag{store.tags.length === 1 ? "" : "s"}</Badge>
            {canWrite && (
              <Button size="sm" onClick={() => setEditing("new")}>
                <Plus className="mr-1.5 size-4" />
                New computed tag
              </Button>
            )}
          </>
        }
      />

      {store.tags.length === 0 ? (
        <EmptyState
          icon={Sigma}
          title="No computed tags yet"
          description="Start with a measure the plant uses, such as overall equipment effectiveness (OEE), carbon equivalent, or a corrected sensor reading."
          cta={
            canWrite ? (
              <Button size="sm" onClick={() => setEditing("new")}>
                <Plus className="mr-1.5 size-4" />
                New computed tag
              </Button>
            ) : undefined
          }
        />
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {store.tags.map((tag) => (
            <ComputedTagCard
              key={tag.id}
              tag={tag}
              canWrite={canWrite}
              onEdit={() => setEditing(tag)}
              onDuplicate={() => store.duplicate(tag.id)}
              onDelete={() => {
                if (confirm(`Delete "${tag.name}"?`)) store.remove(tag.id);
              }}
              onToggle={() => store.update(tag.id, { enabled: !tag.enabled })}
            />
          ))}
        </div>
      )}

      <div className="rounded-lg border border-dashed border-border bg-surface-elevated/40 p-3 text-center text-xs text-on-surface-muted">
        Computed tags appear automatically in the dashboard editor's data-source picker, in
        the correlations matrix, and as targets for alarm rules. They flow through the
        same catalog as raw tags.
      </div>

      {editing && (
        <EditorDrawer
          initial={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSave={(def) => {
            if (editing === "new") {
              store.add(def);
            } else {
              store.update(editing.id, def);
            }
            setEditing(null);
          }}
        />
      )}
    </PageContainer>
  );
}

// ────────────────────────────────────────────────────────────────────
// Card
// ────────────────────────────────────────────────────────────────────

function ComputedTagCard({
  tag,
  canWrite,
  onEdit,
  onDuplicate,
  onDelete,
  onToggle,
}: {
  tag: ComputedTagDef;
  canWrite: boolean;
  onEdit: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  onToggle: () => void;
}) {
  const { output, error } = useLiveCardOutput(tag);
  const updatedAgo = formatDistanceToNow(new Date(tag.updatedAt), { addSuffix: true });

  return (
    <Card className={cn(!tag.enabled && "opacity-60")}>
      <CardHeader className="gap-2 p-4">
        <div className="flex items-start justify-between gap-2">
          <CardTitle className="text-sm">{tag.name}</CardTitle>
          <div className="flex items-center gap-1.5">
            <Badge variant="info" className="font-mono text-[9px]">
              {DATA_KIND_LABEL[tag.outputDataKind]}
            </Badge>
            {canWrite ? (
              <button
                type="button"
                onClick={onToggle}
                className={cn(
                  "rounded-full px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wide",
                  tag.enabled
                    ? "bg-success/15 text-success"
                    : "bg-surface-muted text-on-surface-muted",
                )}
                title={tag.enabled ? "Click to disable" : "Click to enable"}
              >
                {tag.enabled ? "enabled" : "disabled"}
              </button>
            ) : (
              <span
                className={cn(
                  "rounded-full px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wide",
                  tag.enabled
                    ? "bg-success/15 text-success"
                    : "bg-surface-muted text-on-surface-muted",
                )}
              >
                {tag.enabled ? "enabled" : "disabled"}
              </span>
            )}
          </div>
        </div>
        <CardDescription className="flex items-center gap-2 font-mono text-[11px]">
          <span>{tag.id}</span>
          {tag.outputUnit && <span>· {tag.outputUnit}</span>}
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-2 p-4 pt-0">
        <ExpressionRow label="Expression" value={tag.expression} />
        <KVRow label="Inputs">
          <span className="text-on-surface-muted">
            {tag.inputs.length === 0
              ? "—"
              : tag.inputs.map((i) => i.alias).join(" · ")}
          </span>
        </KVRow>
        <KVRow label="Trigger">
          <span className="font-mono text-on-surface-muted">
            {tag.trigger.kind === "on_input_change"
              ? `on input change${tag.trigger.debounceMs ? ` · ${tag.trigger.debounceMs} ms debounce` : ""}`
              : `every ${tag.trigger.periodSeconds} s`}
          </span>
        </KVRow>
        <KVRow label="Output">
          {error ? (
            <span className="flex items-center gap-1 text-danger">
              <AlertTriangle className="size-3" />
              <span className="font-mono text-[11px]">error</span>
            </span>
          ) : output === null ? (
            <span className="font-mono text-on-surface-muted">—</span>
          ) : (
            <span className="font-mono text-on-surface">
              {output.toLocaleString(undefined, { maximumFractionDigits: 2 })}
              {tag.outputUnit ? ` ${tag.outputUnit}` : ""}
            </span>
          )}
        </KVRow>
        <div className="mt-1 flex items-center justify-between gap-2 border-t border-border pt-2">
          <span className="text-[10px] text-on-surface-muted">Updated {updatedAgo}</span>
          {canWrite && (
            <div className="flex items-center gap-1">
              <Button size="sm" variant="ghost" onClick={onDuplicate} title="Duplicate">
                <Copy className="size-3.5" />
              </Button>
              <Button size="sm" variant="ghost" onClick={onDelete} title="Delete">
                <Trash2 className="size-3.5" />
              </Button>
              <Button size="sm" variant="outline" onClick={onEdit}>
                <Edit3 className="mr-1 size-3.5" />
                Edit
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

/** Compute the card's live output once a second — lighter than re-running on every preview tick. */
function useLiveCardOutput(tag: ComputedTagDef) {
  const inputIds = useMemo(() => tag.inputs.map((i) => i.tagId), [tag.inputs]);
  const { values } = useLatestTagValues(inputIds);
  try {
    const compiled = compile(tag.expression);
    const scope: Record<string, number> = {};
    for (const input of tag.inputs) {
      scope[input.alias] = values[input.tagId] ?? NaN;
    }
    const result = compiled.evaluate(scope);
    return { output: Number.isFinite(result) ? result : null, error: null as string | null };
  } catch (err) {
    return { output: null, error: err instanceof Error ? err.message : String(err) };
  }
}

function KVRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-2 text-xs">
      <span className="text-on-surface-muted">{label}</span>
      {children}
    </div>
  );
}

function ExpressionRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-[10px] font-semibold uppercase tracking-wide text-on-surface-muted">
        {label}
      </span>
      <code className="rounded border border-border bg-surface px-2 py-1 font-mono text-[11px] text-on-surface">
        {value || <span className="italic text-on-surface-muted">empty</span>}
      </code>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Editor Drawer
// ────────────────────────────────────────────────────────────────────

interface DraftDef {
  name: string;
  description: string;
  outputUnit: string;
  outputDataKind: DataKind;
  inputs: ComputedTagInput[];
  expression: string;
  trigger: ComputedTagTrigger;
  enabled: boolean;
}

function emptyDraft(): DraftDef {
  return {
    name: "",
    description: "",
    outputUnit: "",
    outputDataKind: "derived_kpi",
    inputs: [],
    expression: "",
    trigger: { kind: "on_input_change" },
    enabled: true,
  };
}

function defToDraft(def: ComputedTagDef): DraftDef {
  return {
    name: def.name,
    description: def.description ?? "",
    outputUnit: def.outputUnit ?? "",
    outputDataKind: def.outputDataKind,
    inputs: [...def.inputs],
    expression: def.expression,
    trigger: { ...def.trigger },
    enabled: def.enabled,
  };
}

function EditorDrawer({
  initial,
  onClose,
  onSave,
}: {
  initial: ComputedTagDef | null;
  onClose: () => void;
  onSave: (def: Omit<ComputedTagDef, "id" | "createdAt" | "updatedAt">) => void;
}) {
  const { tags: catalogTags } = useTagCatalog();
  const [draft, setDraft] = useState<DraftDef>(() =>
    initial ? defToDraft(initial) : emptyDraft(),
  );

  // Expression textarea ref — function-reference chips insert at the
  // current caret position via `insertSnippet`.
  const expressionRef = useRef<HTMLTextAreaElement | null>(null);
  const insertSnippet = (snippet: string) => {
    const el = expressionRef.current;
    if (!el) {
      setDraft((d) => ({ ...d, expression: d.expression + snippet }));
      return;
    }
    const before = el.value.slice(0, el.selectionStart);
    const after = el.value.slice(el.selectionEnd);
    const next = before + snippet + after;
    setDraft((d) => ({ ...d, expression: next }));
    // Restore focus + caret after React re-renders.
    requestAnimationFrame(() => {
      if (!el) return;
      el.focus();
      const pos = before.length + snippet.length;
      el.setSelectionRange(pos, pos);
    });
  };

  // Live validation against the engine.
  const declaredAliases = useMemo(
    () => draft.inputs.map((i) => i.alias).filter(Boolean),
    [draft.inputs],
  );
  const validation = useMemo(
    () => validate(draft.expression, declaredAliases),
    [draft.expression, declaredAliases],
  );

  // Live preview: pull latest values for the declared inputs, then
  // evaluate the (possibly-invalid) expression. Errors are surfaced
  // inline in the preview panel.
  const inputIds = useMemo(() => draft.inputs.map((i) => i.tagId), [draft.inputs]);
  const { values: previewInputs } = useLatestTagValues(inputIds);
  const { output: previewOutput, error: previewError } = useMemo(() => {
    if (!draft.expression.trim()) {
      return { output: null as number | null, error: null as string | null };
    }
    try {
      const compiled = compile(draft.expression);
      const scope: Record<string, number> = {};
      for (const input of draft.inputs) {
        scope[input.alias] = previewInputs[input.tagId] ?? NaN;
      }
      const result = compiled.evaluate(scope);
      if (typeof result !== "number" || !Number.isFinite(result)) {
        return { output: null, error: "Result is not finite" };
      }
      return { output: result, error: null };
    } catch (err) {
      return { output: null, error: err instanceof Error ? err.message : String(err) };
    }
  }, [draft.expression, draft.inputs, previewInputs]);

  const canSave = validation.ok && draft.name.trim().length > 0 && draft.inputs.length > 0;

  const handleSave = () => {
    if (!canSave) return;
    onSave({
      name: draft.name.trim(),
      description: draft.description.trim() || undefined,
      outputUnit: draft.outputUnit.trim() || undefined,
      outputDataKind: draft.outputDataKind,
      inputs: draft.inputs,
      expression: draft.expression,
      trigger: draft.trigger,
      enabled: draft.enabled,
    });
  };

  return (
    <div className="fixed inset-0 z-40 flex" role="dialog" aria-modal="true">
      <button
        type="button"
        aria-label="Close editor"
        className="absolute inset-0 bg-on-surface/40 backdrop-blur-sm"
        onClick={onClose}
      />
      <aside className="relative ml-auto flex h-full w-full max-w-xl flex-col overflow-y-auto bg-surface p-5 shadow-2xl animate-fade-in">
        <div className="mb-4 flex items-center justify-between gap-2">
          <h2 className="text-base font-semibold uppercase tracking-wide text-on-surface">
            {initial ? "Edit computed tag" : "New computed tag"}
          </h2>
          <Button variant="ghost" size="icon" aria-label="Close" onClick={onClose}>
            <X className="size-5" />
          </Button>
        </div>

        {/* Preset gallery — only on new tags */}
        {!initial && (
          <PresetGallery
            onPick={(preset) => {
              const drafted = presetToDraft(preset, catalogTags);
              setDraft({
                name: drafted.name,
                description: drafted.description,
                outputUnit: drafted.outputUnit,
                outputDataKind: drafted.outputDataKind,
                inputs: drafted.inputs,
                expression: drafted.expression,
                trigger: drafted.trigger,
                enabled: true,
              });
            }}
            activePresetId={undefined}
          />
        )}

        {/* Identity */}
        <Section title="Identity">
          <LabelledField label="Name">
            <Input
              value={draft.name}
              onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
              placeholder="Compensated bath temperature"
              aria-invalid={
                draft.name.trim().length === 0 &&
                (draft.inputs.length > 0 || draft.expression.trim().length > 0)
              }
            />
            {/* Inline missing-name error — surfaced at the field once the user
                has started building the tag (not on a pristine empty form), so
                "Save" being disabled is explained here, not only in the footer. */}
            {draft.name.trim().length === 0 &&
              (draft.inputs.length > 0 || draft.expression.trim().length > 0) && (
                <p className="mt-1 flex items-center gap-1 text-[11px] text-danger">
                  <AlertTriangle className="size-3 shrink-0" />
                  Give this computed tag a name to save it.
                </p>
              )}
          </LabelledField>
          <LabelledField label="Description">
            <Input
              value={draft.description}
              onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
              placeholder="One-line explanation"
            />
          </LabelledField>
          <div className="grid grid-cols-2 gap-2">
            <LabelledField label="Output unit">
              <Input
                value={draft.outputUnit}
                onChange={(e) => setDraft((d) => ({ ...d, outputUnit: e.target.value }))}
                placeholder="°C / % / kWh"
              />
            </LabelledField>
            <LabelledField label="Data kind">
              <select
                value={draft.outputDataKind}
                onChange={(e) =>
                  setDraft((d) => ({ ...d, outputDataKind: e.target.value as DataKind }))
                }
                className="h-8 w-full rounded-md border border-border bg-surface px-2 text-xs text-on-surface"
              >
                {ALL_DATA_KINDS.map((k) => (
                  <option key={k} value={k}>
                    {DATA_KIND_LABEL[k]}
                  </option>
                ))}
              </select>
            </LabelledField>
          </div>
        </Section>

        {/* Inputs */}
        <Section title="Inputs">
          <p className="text-[11px] text-on-surface-muted">
            Pick the raw or computed tags to feed in. Each gets an alias you reference in
            the expression.
          </p>
          <InputsEditor
            value={draft.inputs}
            onChange={(inputs) => setDraft((d) => ({ ...d, inputs }))}
          />
        </Section>

        {/* Expression */}
        <Section title="Expression">
          <textarea
            ref={expressionRef}
            value={draft.expression}
            onChange={(e) => setDraft((d) => ({ ...d, expression: e.target.value }))}
            placeholder={"T_raw - 0.024 * (T_raw - T_amb)"}
            spellCheck={false}
            rows={3}
            className={cn(
              "w-full rounded-md border bg-surface p-2 font-mono text-sm text-on-surface focus:outline-none",
              validation.parseError
                ? "border-danger/50 focus:border-danger"
                : "border-border focus:border-brand",
            )}
          />
          <ValidationFeedback validation={validation} />
          <FunctionReference onInsert={insertSnippet} />
        </Section>

        {/* Trigger */}
        <Section title="Trigger">
          <TriggerEditor
            value={draft.trigger}
            onChange={(trigger) => setDraft((d) => ({ ...d, trigger }))}
          />
        </Section>

        {/* Live preview */}
        <Section title="Live preview">
          <LivePreviewPanel
            inputs={draft.inputs}
            inputValues={previewInputs}
            output={previewOutput}
            error={previewError}
            unit={draft.outputUnit}
            catalog={catalogTags}
          />
        </Section>

        {/* Footer actions — sticky */}
        <div className="sticky bottom-0 mt-auto flex items-center justify-between gap-2 border-t border-border bg-surface pt-3">
          <div className="flex items-center gap-2 text-[11px] text-on-surface-muted">
            {validation.ok ? (
              <>
                <CheckCircle2 className="size-3.5 text-success" />
                <span>Valid expression</span>
              </>
            ) : (
              <>
                <AlertTriangle className="size-3.5 text-warning" />
                <span>Resolve issues before saving</span>
              </>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={onClose}>
              Cancel
            </Button>
            <Button size="sm" onClick={handleSave} disabled={!canSave}>
              Save tag
            </Button>
          </div>
        </div>
      </aside>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Editor sub-components
// ────────────────────────────────────────────────────────────────────

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mb-4 flex flex-col gap-2 rounded-md border border-border bg-surface-elevated p-3">
      <h3 className="text-[10px] font-semibold uppercase tracking-wide text-on-surface-muted">
        {title}
      </h3>
      <div className="flex flex-col gap-2">{children}</div>
    </section>
  );
}

function LabelledField({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[10px] font-semibold uppercase tracking-wide text-on-surface-muted">
        {label}
      </span>
      {children}
    </label>
  );
}

function InputsEditor({
  value,
  onChange,
}: {
  value: ComputedTagInput[];
  onChange: (next: ComputedTagInput[]) => void;
}) {
  const [search, setSearch] = useState("");
  const { tags: allTags, truncated } = useTagCatalog();

  const catalog = useMemo<CatalogTag[]>(() => {
    const q = search.toLowerCase();
    return allTags.filter(
      (t) =>
        !value.find((v) => v.tagId === t.id) &&
        (t.id.toLowerCase().includes(q) ||
          t.displayName.toLowerCase().includes(q)),
    );
  }, [allTags, search, value]);

  const addInput = (tag: CatalogTag) => {
    const alias = makeAliasFromTag(tag, value);
    onChange([...value, { tagId: tag.id, alias }]);
    setSearch("");
  };

  const updateAlias = (idx: number, alias: string) => {
    const next = [...value];
    next[idx] = { ...next[idx], alias };
    onChange(next);
  };

  const removeInput = (idx: number) => {
    const next = value.filter((_, i) => i !== idx);
    onChange(next);
  };

  return (
    <div className="flex flex-col gap-2">
      {value.length > 0 && (
        <div className="flex flex-col gap-1">
          {value.map((input, idx) => {
            const tag = allTags.find((t) => t.id === input.tagId);
            return (
              <div
                key={input.tagId}
                className="flex items-center gap-2 rounded-md border border-border bg-surface p-2 text-xs"
              >
                <span className="font-mono text-on-surface-muted">{input.tagId}</span>
                {tag && <span className="text-on-surface-muted">·</span>}
                {tag && (
                  <span className="flex-1 truncate text-on-surface-muted">
                    {tag.displayName}
                  </span>
                )}
                <span className="text-on-surface-muted">→</span>
                <Input
                  value={input.alias}
                  onChange={(e) => updateAlias(idx, e.target.value)}
                  className="h-6 w-28 font-mono text-xs"
                  placeholder="alias"
                />
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-6"
                  onClick={() => removeInput(idx)}
                  aria-label="Remove input"
                >
                  <X className="size-3.5" />
                </Button>
              </div>
            );
          })}
        </div>
      )}
      <div className="relative">
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search tag catalog…"
          className="h-7 text-xs"
        />
      </div>
      {truncated && (
        <p className="text-[11px] text-on-surface-muted">
          Showing the first {ORG_TAG_PAGE_CAP} tags — the catalogue is capped per
          request, so a tag beyond that won&apos;t appear here yet.
        </p>
      )}
      {search && catalog.length > 0 && (
        <div className="flex max-h-48 flex-col gap-1 overflow-y-auto rounded-md border border-border bg-surface p-1">
          {catalog.slice(0, 12).map((tag) => (
            <button
              key={tag.id}
              type="button"
              onClick={() => addInput(tag)}
              className="flex items-center justify-between gap-2 rounded px-2 py-1 text-left text-xs hover:bg-brand-muted"
            >
              <div className="flex flex-col">
                <span className="text-on-surface">{tag.displayName}</span>
                <span className="font-mono text-[10px] text-on-surface-muted">
                  {tag.id} {tag.unit && `· ${tag.unit}`}
                </span>
              </div>
              {tag.dataKind && (
                <Badge variant="secondary" className="text-[9px]">
                  {DATA_KIND_LABEL[tag.dataKind]}
                </Badge>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ValidationFeedback({
  validation,
}: {
  validation: ReturnType<typeof validate>;
}) {
  if (validation.ok) return null;
  if (validation.parseError) {
    return (
      <div className="flex items-start gap-1.5 text-[11px] text-danger">
        <AlertTriangle className="size-3.5 shrink-0" />
        <span className="font-mono">{validation.parseError}</span>
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-1 text-[11px]">
      {validation.undeclaredSymbols && validation.undeclaredSymbols.length > 0 && (
        <div className="flex items-start gap-1.5 text-warning">
          <AlertTriangle className="size-3.5 shrink-0" />
          <span>
            Undeclared symbol{validation.undeclaredSymbols.length > 1 ? "s" : ""}:{" "}
            <span className="font-mono">{validation.undeclaredSymbols.join(", ")}</span>{" "}
            — add as input alias or remove from the expression.
          </span>
        </div>
      )}
      {validation.unusedAliases && validation.unusedAliases.length > 0 && (
        <div className="flex items-start gap-1.5 text-on-surface-muted">
          <AlertTriangle className="size-3.5 shrink-0" />
          <span>
            Unused alias{validation.unusedAliases.length > 1 ? "es" : ""}:{" "}
            <span className="font-mono">{validation.unusedAliases.join(", ")}</span> —
            input is declared but not referenced.
          </span>
        </div>
      )}
      {validation.cycle && (
        <div className="flex items-start gap-1.5 text-danger">
          <AlertTriangle className="size-3.5 shrink-0" />
          <span>This tag references itself — would create an infinite loop.</span>
        </div>
      )}
    </div>
  );
}

/**
 * Categorised function-reference under the expression textarea — chips
 * are click-to-insert. Stateful funcs render with a "Preview" badge.
 */
function FunctionReference({
  onInsert,
}: {
  onInsert: (snippet: string) => void;
}) {
  return (
    <details className="text-[10px]">
      <summary className="cursor-pointer text-on-surface-muted hover:text-on-surface">
        <Code2 className="mr-1 inline size-3" /> Function reference
      </summary>
      <div className="mt-2 flex flex-col gap-1.5">
        {FUNCTION_GROUPS.map((g) => (
          <div key={g.label} className="flex flex-wrap items-center gap-1">
            <span className="w-32 shrink-0 text-[10px] font-semibold uppercase tracking-wide text-on-surface-muted">
              {g.label}
            </span>
            {g.entries.map((e) => (
              <button
                key={e.chip}
                type="button"
                onClick={() => onInsert(e.chip)}
                title={e.hint ?? e.chip}
                className={cn(
                  "inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 font-mono text-[10px] transition-colors",
                  "border-border bg-surface text-on-surface hover:border-brand hover:bg-brand-muted",
                  e.preview && "border-warning/40 bg-warning/5",
                )}
              >
                {e.chip}
                {e.preview && (
                  <span className="rounded-full bg-warning/20 px-1 py-0 text-[8px] font-semibold uppercase tracking-wider text-warning">
                    preview
                  </span>
                )}
              </button>
            ))}
          </div>
        ))}
        <p className="text-[10px] italic text-on-surface-muted">
          <span className="font-semibold text-warning">Preview</span> functions are
          client-side only in Phase 0; semantics are validated against the server's
          window-buffer worker in Phase 1.
        </p>
      </div>
    </details>
  );
}

/**
 * "Start from preset" gallery — shown at the top of the New-tag drawer.
 * Picking a tile populates the draft with the recipe's expression +
 * suggested input aliases. Users still pick the actual tag ids.
 */
function PresetGallery({
  onPick,
  activePresetId,
}: {
  onPick: (preset: ComputedTagPreset) => void;
  activePresetId?: string;
}) {
  return (
    <details className="mb-4 rounded-md border border-border bg-surface-elevated p-3" open>
      <summary className="cursor-pointer text-[10px] font-semibold uppercase tracking-wide text-on-surface-muted">
        <Wand2 className="mr-1 inline size-3" />
        Start from preset
      </summary>
      <div className="mt-2 grid grid-cols-2 gap-2">
        {PRESETS.map((p) => {
          const active = activePresetId === p.id;
          return (
            <button
              key={p.id}
              type="button"
              onClick={() => onPick(p)}
              className={cn(
                "flex flex-col gap-0.5 rounded-md border p-2 text-left text-[11px] transition-colors",
                active
                  ? "border-brand bg-brand-muted"
                  : "border-border bg-surface hover:border-brand/60 hover:bg-brand-muted/40",
              )}
            >
              <div className="flex items-center justify-between gap-1">
                <span className="font-medium text-on-surface">{p.name}</span>
                <Sparkles className="size-3 text-brand opacity-60" />
              </div>
              <span className="text-[10px] text-on-surface-muted">{p.blurb}</span>
            </button>
          );
        })}
      </div>
      <p className="mt-2 text-[10px] italic text-on-surface-muted">
        Picking a preset overwrites the current draft. Fill in the input tag bindings
        afterwards; the expression and aliases come pre-loaded.
      </p>
    </details>
  );
}

function TriggerEditor({
  value,
  onChange,
}: {
  value: ComputedTagTrigger;
  onChange: (next: ComputedTagTrigger) => void;
}) {
  return (
    <div className="flex flex-col gap-2 text-xs">
      <label className="flex items-center gap-2">
        <input
          type="radio"
          checked={value.kind === "on_input_change"}
          onChange={() => onChange({ kind: "on_input_change" })}
        />
        <span>On any input change</span>
        {value.kind === "on_input_change" && (
          <>
            <span className="ml-2 text-on-surface-muted">debounce</span>
            <input
              type="number"
              min={0}
              value={value.debounceMs ?? 0}
              onChange={(e) =>
                onChange({ kind: "on_input_change", debounceMs: Number(e.target.value) || undefined })
              }
              className="h-6 w-16 rounded border border-border bg-surface px-1 font-mono text-xs"
            />
            <span className="text-on-surface-muted">ms</span>
          </>
        )}
      </label>
      <label className="flex items-center gap-2">
        <input
          type="radio"
          checked={value.kind === "scheduled"}
          onChange={() => onChange({ kind: "scheduled", periodSeconds: 30 })}
        />
        <span>Every</span>
        {value.kind === "scheduled" && (
          <>
            <input
              type="number"
              min={1}
              value={value.periodSeconds}
              onChange={(e) =>
                onChange({ kind: "scheduled", periodSeconds: Math.max(1, Number(e.target.value)) })
              }
              className="h-6 w-16 rounded border border-border bg-surface px-1 font-mono text-xs"
            />
            <span className="text-on-surface-muted">seconds</span>
          </>
        )}
        {value.kind !== "scheduled" && <span className="text-on-surface-muted">— seconds</span>}
      </label>
    </div>
  );
}

function LivePreviewPanel({
  inputs,
  inputValues,
  output,
  error,
  unit,
  catalog = [],
}: {
  inputs: ComputedTagInput[];
  inputValues: Record<string, number>;
  output: number | null;
  error: string | null;
  unit?: string;
  /** Resolved input tags, so the panel can label rows by name. */
  catalog?: CatalogTag[];
}) {
  return (
    <div className="flex flex-col gap-2">
      {/* Inputs snapshot */}
      <div className="grid grid-cols-2 gap-1.5">
        {inputs.length === 0 ? (
          <div className="col-span-2 text-[11px] italic text-on-surface-muted">
            Add inputs above to see live values.
          </div>
        ) : (
          inputs.map((input) => {
            const v = inputValues[input.tagId];
            const cat = catalog.find((t) => t.id === input.tagId);
            return (
              <div
                key={input.tagId}
                className="flex items-baseline justify-between gap-1 rounded border border-border bg-surface px-2 py-1 text-[11px]"
              >
                <span className="font-mono text-on-surface">{input.alias}</span>
                <span className="font-mono text-on-surface-muted">
                  {typeof v === "number"
                    ? v.toLocaleString(undefined, { maximumFractionDigits: 2 })
                    : "—"}
                  {cat?.unit ? ` ${cat.unit}` : ""}
                </span>
              </div>
            );
          })
        )}
      </div>

      {/* Output */}
      <div className="flex items-center justify-between rounded-md border border-brand/40 bg-brand-muted/40 px-3 py-2">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-wide text-on-surface-muted">
          <History className="size-3.5" />
          Current output
        </div>
        <div className="flex items-baseline gap-1">
          {error ? (
            <span className="font-mono text-sm text-danger">{error}</span>
          ) : output === null ? (
            <span className="font-mono text-sm text-on-surface-muted">—</span>
          ) : (
            <>
              <span className="font-mono text-lg font-semibold text-on-surface">
                {output.toLocaleString(undefined, { maximumFractionDigits: 3 })}
              </span>
              {unit && <span className="text-[11px] text-on-surface-muted">{unit}</span>}
            </>
          )}
        </div>
      </div>

      <p className="text-[10px] text-on-surface-muted">
        Values drift each second in the demo. In production, this preview reads from
        <code className="ml-1 font-mono">/v1/iot/tags/&lbrace;id&rbrace;/latest</code>.
      </p>
    </div>
  );
}

// Quiet the unused-import lint for icons used only in JSX text.
void useEffect;
